import { CommonModule, NgOptimizedImage } from "@angular/common";
import {
  Component,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  signal,
  input
} from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { Capacitor } from "@capacitor/core";
import { IonicModule } from "@ionic/angular";
import { SavePassword } from "capacitor-ios-autofill-save-password";

type AuthProps = {
  forename: string;
  surname: string;
  email: string;
  password: string;
};

@Component({
    selector: "app-auth-register-form",
    templateUrl: "./auth-register-form.component.html",
    styleUrls: ["./auth-register-form.component.scss"],
    imports: [ReactiveFormsModule, CommonModule, IonicModule, NgOptimizedImage],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthRegisterFormComponent {
  isSignIn = signal<boolean>(false);
  authRegisterForm = this.fb.group(
    {
      forename: ["", [Validators.required]],
      surname: ["", [Validators.required]],
      email: ["", [Validators.required, Validators.email]],
      password: [
        "",
        [
          Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z]).+$'),
          Validators.minLength(6)
        ],
      ],
      repeatPassword: [
        "",
        [
          Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z]).+$'),
          Validators.minLength(6)
        ],
      ],
    },
    {
      validators: [this.passwordMatchValidator("password", "repeatPassword")],
    }
  );

  readonly resetOnSubmit = input(false);
  readonly resetOnAuthModeChange = input(false);
  readonly loading = input<boolean>(false);
  @Output() modeChange = new EventEmitter<string>();
  @Output() submit = new EventEmitter<AuthProps>();

  constructor(private fb: FormBuilder) {}

  getPasswordErrorText() {
    const password = this.authRegisterForm.get('password');
    if (password?.errors?.['required']) {
      return 'Passwort ist erforderlich';
    }
    if (password?.errors?.['minlength']) {
      return 'Passwort muss mindestens 6 Zeichen lang sein';
    }
    if (password?.errors?.['pattern']) {
      return 'Passwort muss mindestens einen Buchstaben und eine Zahl enthalten';
    }
    return '';
  }

  getRepeatPasswordErrorText() {
    const repeatPassword = this.authRegisterForm.get('repeatPassword');
    if (repeatPassword?.errors?.['passwordMismatch']) {
      return 'Passwort stimmt nicht überein';
    } else if (repeatPassword?.errors?.['required']) {
      return 'Passwort ist erforderlich';
    } else if (repeatPassword?.errors?.['pattern']) {
      return 'Passwort muss mindestens einen Buchstaben und eine Zahl enthalten';
    } else if (repeatPassword?.errors?.['minlength']) {
      return 'Passwort muss mindestens 6 Zeichen lang sein';
    }
    return '';
  }

  passwordMatchValidator(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (matchingControl.errors && !matchingControl.errors.passwordMismatch) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ passwordMismatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  handleChange(e: Event) {
    e.preventDefault();
    this.isSignIn.set(!this.isSignIn());
    this.modeChange.emit(this.isSignIn() ? 'signIn' : 'register');
    if (this.resetOnAuthModeChange()) {
      this.authRegisterForm.reset();
    }
  }

  async handleSubmit() {
    if (this.authRegisterForm.invalid) {
      return;
    }
    const forename = this.authRegisterForm.value["forename"];
    const surname = this.authRegisterForm.value["surname"];
    const email = this.authRegisterForm.value["email"];
    const password = this.authRegisterForm.value["password"];
    this.submit.emit({ forename, surname, email, password });
    if (this.resetOnSubmit()) {
      this.authRegisterForm.reset();
    }
    if (Capacitor.getPlatform() === "ios") {
      await SavePassword.promptDialog({
        username: email,
        password: password,
      });
    }
  }
}
